import { Box, Button, Divider, Fade, Grid, Popper, PopperPlacementType, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Delete, ErrorOutline, InboxOutlined } from '@material-ui/icons';
import { WarningAmberOutlined } from '@mui/icons-material';
import { createSnackbar } from 'Core/ducks/snackbar';
import { isLoaded } from 'Core/models/Async';
import { getCompensationPayload, getHeapedRule, getLevelRule } from 'Quoting/api/apiHelpers';
import { useQuoteField } from 'Quoting/components/hooks/useHandleQuoteFieldChange';
import {
  deleteCompensationFlag,
  getCompensationInit,
  getValidatePayloadDraft,
  setCompensationValue,
  setIsCompensationUpdated,
} from 'Quoting/ducks/compensation';
import { CompensationPayload, Heaped, Level, RetrieveCompensationPayload } from 'Quoting/models/DTOs/CompensationPayload';
import { SplitPart } from 'Quoting/models/DTOs/PremiumSplit';
import { AgentRoleType, AgentRoleTypeValue } from 'Quoting/models/enum';
import { isDefined } from 'Quoting/utils/utilities';
import { List as ImmutableList } from 'immutable';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ReducerState } from 'rootReducer';
import { QuoteFormElement } from '../../shared/QuoteFormElement';
import { CompensationTypeForm } from './CompensationTypeForm';
import { SearchAgencyField } from './SearchAgency';
import { themeColors } from 'Theme';

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(1),
  },
  label: {
    color: '#474747',
    fontSize: '0.875rem',
    lineHeight: '1.05rem',
  },
  noRecordParent: {
    display: 'flex',
    height: '25vh',
    textAlign: 'center',
    padding: '15px 5px',
    width: '100%',
  },
  alignRecordCenter: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    width: 'auto',
    lineHeight: '30px',
  },
  typeField: {
    display: 'flex',
    alignItems: 'center',
  },
  rootCompensation: {
    fontFamily: 'Roboto, sans-serif',
    fontSize: '0.875rem',
    lineHeight: '1.25rem',
    color: '#444444',
    padding: theme.spacing(2),
    overflow: 'hidden',
  },
  gridContainer: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  gridHeader: {
    fontWeight: 'bold',
    textAlign: 'center',
    padding: theme.spacing(1),
  },
  gridCell: {
    textAlign: 'center',
    padding: theme.spacing(1),
    borderBottom: '1px solid #ccc',
  },
  planError: {
    backgroundColor: themeColors.red_50,
    border: `1px solid ${themeColors.red_50}`,
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0.8rem 2.5rem',
    lineHeight: '1.25rem',
    color: `${themeColors.gray_800} !important`,
    '& svg': {
      marginRight: '10px',
      color: themeColors.red_600,
    },
  },
  disabledText: {
    color: 'rgba(0, 0, 0, 0.25)',
  },
  alignSelf: {
    alignSelf: 'center',
  },
}));

const agentRoleOptions = [
  { code: AgentRoleType.AgentOfRecord, displayValue: AgentRoleTypeValue.AgentOfRecord },
  { code: AgentRoleType.ServicingAgent, displayValue: AgentRoleTypeValue.ServicingAgent },
  { code: AgentRoleType.WritingAgent, displayValue: AgentRoleTypeValue.WritingAgent },
];

const commissionTypeOptions = [
  {
    displayValue: 'Commission',
    code: 'Commission',
  },
  {
    displayValue: 'Fee',
    code: 'Fee',
  },
];

export const EditAgenciesCompensation = ({
  splitPart,
  isCompensationFormVisible,
  onCancel,
  onConfirm,
  setCompensationFormVisible,
}: {
  splitPart: SplitPart;
  isCompensationFormVisible: boolean;
  onCancel: (isEdit: boolean, isExpend: boolean) => void;
  onConfirm: () => void;
  setCompensationFormVisible: (visible: boolean) => void;
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { getQuoteFieldValue } = useQuoteField();
  const { initResponse, compensationValue, searchValue, compensationPayload } = useSelector((state: ReducerState) => state.compensation);
  const [open, setOpen] = useState(false);
  const [placement, setPlacement] = useState<PopperPlacementType>();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const canBeOpen = open && Boolean(anchorEl);
  const id = canBeOpen ? 'transition-popper' : undefined;
  const { quote } = useSelector((state: ReducerState) => state.quoteForm);
  const [disableConfirm, setDisableConfirm] = useState(true);
  const handleAddNewCompensation = () => {
    dispatch(getCompensationInit());
    setCompensationFormVisible(true);
  };

  const handleDelete = () => {
    dispatch(setCompensationValue({ type: '', rate: '' }));
    dispatch(deleteCompensationFlag(true));
    dispatch(setIsCompensationUpdated(true));
    setOpen(false);
    setCompensationFormVisible(false);
    onCancel(true, true);
  };

  const handleClickDelete = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setOpen(previousOpen => !previousOpen);
    setPlacement('bottom-end');
  };

  const handlePopper = (open: boolean) => {
    setOpen(open);
  };

  const OnCompensationConfirm = () => {
    if (compensationValue.type && compensationValue.rate) {
      onConfirm();
      if (isLoaded(compensationPayload)) {
        const payload: CompensationPayload = compensationPayload.Data;
        payload.contract.processingDefinition.calculationDefinition.rule =
          compensationValue.type === 'Level' ? getLevelRule(compensationValue.rate) as Level : getHeapedRule(compensationValue.rate) as Heaped;
        dispatch(getValidatePayloadDraft(payload));
      }
      else if ((isLoaded(searchValue) || isLoaded(initResponse)) && isLoaded(quote)) {
        const policyNumberValue: string = getQuoteFieldValue<string>('policyNumber') ?? '';

        const compPayload: RetrieveCompensationPayload = {
          policyNumberValue: policyNumberValue,
          start:
            getQuoteFieldValue<string>('rateEffectiveDate')?.split('T')[0] ??
            getQuoteFieldValue<string>('termDetails.termEffectiveDate')?.split('T')[0] ??
            '',
          //@ts-ignore
          initResponse: isLoaded(initResponse) ? initResponse.Data : searchValue.Data[0],
          premiumSplitPercentage: splitPart?.premiumSplitPct * 100,
          productCode: getQuoteFieldValue<string>('productCd') ?? '',
          code: getQuoteFieldValue<string>('agencyDetails[0].agencyName') ?? '',
          premiumSplitId: splitPart?._key.id,
          rate: compensationValue.rate,
          compType: compensationValue.type,
        };

        const payload: CompensationPayload = getCompensationPayload(compPayload);

        dispatch(getValidatePayloadDraft(payload));
      }
    } else if (!compensationValue.type) {
      dispatch(createSnackbar('Processing Type is required'));
    } else if (!compensationValue.rate) {
      dispatch(createSnackbar('Rate is required'));
    }
  };

  const OnCompensationCancel = () => {
    onCancel(false, false);
  };

  const noPlansAdded: boolean =
    !isDefined(getQuoteFieldValue<ImmutableList<any>>('benefitPackagingDetail.plans')) ||
    (isDefined(getQuoteFieldValue<ImmutableList<any>>('benefitPackagingDetail.plans')) &&
      getQuoteFieldValue<ImmutableList<any>>('benefitPackagingDetail.plans')?.size === 0);

  return (
    <>
      <div className={classes.root}>
        <Grid container spacing={2} style={{ marginBottom: 16 }}>
          <SearchAgencyField agencyName={splitPart?.brokerName} />
          <Grid item xs={12} sm={4}>
            <QuoteFormElement
              elementType="Select"
              options={commissionTypeOptions}
              id="compensation-type-2"
              label="Compensation Type"
              required
              value={splitPart?.premiumSplitDetails[0].compensationTypeCd}
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <QuoteFormElement
              elementType="Select"
              options={agentRoleOptions}
              id="role"
              required
              label="Role"
              value={splitPart?.premiumSplitDetails[0]?.broker.roleCd}
              disabled
            />
          </Grid>
        </Grid>
        <Grid container spacing={2} style={{ marginBottom: 16 }}>
          <Grid item xs={12} sm={4}>
            <QuoteFormElement
              elementType="Text"
              id="premium-split"
              label="Premium Split %"
              required
              value={`${splitPart?.premiumSplitPct * 100}%`}
              disabled
            />
          </Grid>
        </Grid>
      </div>

      <Typography
        style={{
          fontSize: '0.9rem',
          lineHeight: '1.2rem',
          color: '#333333',
          marginLeft: '5px',
          fontWeight: 600,
          padding: '5px 0',
        }}
      >
        Compensation
      </Typography>
      <div style={{ display: 'flex', flexDirection: 'row' }}>
        <Divider style={{ width: '100%' }} />
        {isCompensationFormVisible && (
          <div>
            <Delete
              style={{ color: 'rgba(0, 0, 0, 0.54)', transform: 'translate(1vw, -1.5vh) scale(1)' }}
              onClick={e => handleClickDelete((e as unknown) as React.MouseEvent<HTMLElement, MouseEvent>)}
            />
            <Popper id={id} open={open} anchorEl={anchorEl} transition placement={placement}>
              {({ TransitionProps }) => (
                <Fade {...TransitionProps} timeout={350}>
                  <Box
                    sx={{
                      bgcolor: 'background.paper',
                      width: '20vw',
                      height: '20vh',
                    }}
                    style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)', padding: '15px' }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        height: '100%',
                      }}
                    >
                      <WarningAmberOutlined style={{ color: '#f28e0b', marginRight: '10px' }} />
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                          height: '100%',
                        }}
                      >
                        Are you sure you want to delete this Compensation Contact?
                        <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end' }}>
                          <Button
                            variant="contained"
                            color="default"
                            size="small"
                            style={{ marginRight: '20px' }}
                            onClick={() => handlePopper(false)}
                          >
                            Cancel
                          </Button>
                          <Button variant="contained" color="primary" size="small" onClick={handleDelete}>
                            Delete
                          </Button>
                        </div>
                      </div>
                    </div>
                  </Box>
                </Fade>
              )}
            </Popper>
          </div>
        )}
      </div>
      {!isCompensationFormVisible ? (
        <div className={classes.noRecordParent} style={{ display: 'inline-block' }}>
          <InboxOutlined style={{ color: '#D9D9D9', transform: 'scale(2)' }} />
          <br />
          <div className={classes.alignRecordCenter}>There are no items here</div>
          {noPlansAdded && (
            <div className={`${classes.alignRecordCenter} ${classes.disabledText}`}>
              Agency and Compensation Type should be specified
            </div>
          )}
          <br />
          <div className={classes.alignRecordCenter}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleAddNewCompensation}
              disabled={noPlansAdded}
              className={classes.alignSelf}
            >
              Add Compensation Type
            </Button>
          </div>
          <br />
          {noPlansAdded && (
            <div className={classes.alignRecordCenter}>
              <Typography className={`${classes.planError} ${classes.alignSelf}`} style={{ width: 'fit-content' }}>
                <ErrorOutline />A plan needs to be selected before adding Compensation
              </Typography>
            </div>
          )}
        </div>
      ) : (
        <CompensationTypeForm disabled={false} isConfirmDisabled={isDisabled => setDisableConfirm(isDisabled)} />
      )}

      <div style={{ marginTop: '16px' }}>
        <Button
          variant="contained"
          color="primary"
          style={{ marginRight: '8px' }}
          onClick={OnCompensationConfirm}
          disabled={disableConfirm}
        >
          Confirm
        </Button>
        <Button variant="contained" onClick={OnCompensationCancel}>
          Cancel
        </Button>
      </div>
    </>
  );
};
